/**
 * @fileoverview Top-level renderer entry point.
 * @module @firesquirrel/inspection-renderer
 *
 * Responsibilities:
 * - Compose every Phase 1-5 piece into the canonical entry point that
 *   consuming surfaces (system-web preview, tenant-web preview,
 *   tenant-web conductor, mobile-pwa conductor) mount.
 * - Subscribe to the host-injected `PersistAdapter` and re-render on
 *   changes.
 * - Drive the rules engine v2 (`evaluateFormRulesWithTrace`) on every
 *   change, producing the `ResolvedFormStateV2` map + rule trace that
 *   `<SectionRenderer>` and `<DataSlideIn>` both consume.
 * - Render forms either via `<SectionRenderer>` (formKind: `standard`/
 *   `header`) or via `<ModuleRenderer>` (formKind: `module`).
 *
 * Dependencies:
 * - React 18 (peer).
 * - All Phase 1-5 components + adapters + contracts.
 *
 * Architecture:
 * - Stateless apart from the `currentFormId` tab and the bound
 *   subscription tick. Persisted state lives in `PersistAdapter`.
 * - Wrapped in `<RendererProviders>` so AttachmentPicker + ResolveContext
 *   are injected to every nested widget.
 *
 * @component InspectionRenderer
 *
 * @contract InspectionRendererProps
 * Purpose: prop contract for the canonical renderer entry point.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  InspectionForm,
  InspectionRule,
  ResolvedFormStateV2,
  RuleTraceEntry,
  FieldResponse,
} from '@firesquirrel/core-domain';
import {
  evaluateAllFormRulesWithTrace,
  validateInspectionForCompletion,
  type CompletionVerdict,
} from '@firesquirrel/core-domain';
import { buildCompletionValidationInput } from './validation/completionInput';
import { SectionRenderer } from './sections/SectionRenderer';
import { ModuleRenderer } from './modules/ModuleRenderer';
import { DataSlideIn } from './data-panel/DataSlideIn';
import { RendererProviders } from './contracts/rendererContext';
import type { PersistAdapter } from './adapters/persistAdapter';
import type { AttachmentPicker } from './contracts/attachmentPicker';
import type { ResolveContext } from './contracts/resolveContext';
import type { ThumbnailResolver } from './contracts/rendererContext';
import type { ContactEntry } from '@firesquirrel/core-domain';
import type { ContactChange } from './violations/types';
import type { GetDensityOpts } from './tokens/densityTokens';
import { ValidationSummary } from './validation/ValidationSummary';
import { getFieldIssues, type FieldIssue } from './validation/fieldIssues';
import {
  ViolationsCaptureProvider,
  type ViolationsCaptureContextValue,
} from './violations/violationsCaptureContext';
import {
  allowsLegacyViolationFallback,
  findViolationForMapping,
} from './violations/findViolationForMapping';
import type { LoggedViolation } from './violations/types';
import {
  getPrimaryInspectorFromResponses,
  type InspectorRosterEntry,
} from './inspectors/inspectorSelection';

export interface InspectionRendererProps {
  mode: 'preview' | 'conduct';
  forms: InspectionForm[];
  templateRules?: readonly InspectionRule[];
  persistAdapter: PersistAdapter;
  attachmentPicker?: AttachmentPicker;
  resolveContext?: ResolveContext;
  caseId: string;
  inspectorId: string;
  /** Event-level roster used by `inspectors` widgets to select one primary. */
  inspectorRoster?: readonly InspectorRosterEntry[];
  entityName?: string;
  templateName?: string;
  currentEventIndex?: number;
  bulkPilotMode?: boolean;
  density?: GetDensityOpts;
  readOnly?: boolean;
  /** Initial contact pool for the contacts module. */
  contactPool?: ContactEntry[];
  /** Bubble Contact-list changes to the host (typically re-routed back to PersistAdapter). */
  onModuleContactsChange?: (contacts: ContactEntry[], changes: ContactChange[]) => void;
  /** When set, renders the Data slide-in panel in a 2-column layout. */
  showDataSlideIn?: boolean;
  /** Optional reset handler shown only in preview mode. */
  onReset?: () => void;
  /**
   * Host-reported connectivity. When `true`, Camera/Photo/Signature/Location
   * widgets render their "offline" pill and disable capture controls that
   * require live network. The renderer also surfaces this via `useIsOffline()`
   * for widget-internal branches.
   */
  isOffline?: boolean;
  /** Host-injected thumbnail resolver for visual photo previews in Camera/Photo widgets. */
  thumbnailResolver?: ThumbnailResolver;
  /**
   * Opaque key forwarded to every `<FieldRenderer>`. Change it after calling
   * `persistAdapter.reset()` to trigger default-value re-application on all
   * fields — identical to an initial load. Omit in conduct mode.
   */
  resetKey?: string | number;
  /**
   * Bubbles the canonical completion verdict to the host so the host's
   * "Complete Inspection" control can hard-block when any validation issue
   * exists. The verdict is computed by the SINGLE shared
   * `validateInspectionForCompletion` process in `@firesquirrel/core-domain`
   * — hosts MUST NOT re-implement completion gating.
   */
  onCompletionVerdictChange?: (verdict: CompletionVerdict) => void;
}

export function InspectionRenderer(props: InspectionRendererProps): ReactNode {
  const {
    mode, forms, templateRules, persistAdapter, attachmentPicker, resolveContext,
    caseId, inspectorId, inspectorRoster, entityName, templateName,
    currentEventIndex = 0, bulkPilotMode, density, readOnly, contactPool,
    onModuleContactsChange, showDataSlideIn, onReset, isOffline, thumbnailResolver, resetKey,
    onCompletionVerdictChange,
  } = props;

  // Subscribe to adapter changes to force re-renders. The adapter is
  // the source of truth; we never copy its state into local state.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    return persistAdapter.subscribe(() => setTick((t) => t + 1));
  }, [persistAdapter]);

  const initialVisibleForms = useMemo(
    () => forms.filter((f) => f.initialVisibility !== 'hidden'),
    [forms],
  );
  const [activeFormId, setActiveFormId] = useState<string>(initialVisibleForms[0]?._id ?? '');

  // Build the global response map for engine v2. Engine treats the
  // namespace as flat (one fieldId → FieldResponse map across the whole
  // template); the renderer flattens here.
  const allResponses = useMemo(() => {
    // `tick` is read so the memo recomputes on every adapter notify.
    void tick;
    const m: Record<string, FieldResponse> = {};
    for (const f of forms) {
      const vals = persistAdapter.getValues(f._id);
      for (const [id, fr] of Object.entries(vals)) m[id] = fr;
    }
    return m;
  }, [forms, persistAdapter, tick]);

  // Engine v2 evaluation (per-form rules + template rules + cross-form deps).
  const resolvedStates: Record<string, ResolvedFormStateV2> = useMemo(() => {
    return evaluateAllFormRulesWithTrace(forms, allResponses, templateRules);
  }, [forms, allResponses, templateRules]);

  // Combined trace: union of every form's trace (each is already deduped within form).
  const rulesTrace: RuleTraceEntry[] = useMemo(() => {
    const out: RuleTraceEntry[] = [];
    const seen = new Set<string>();
    for (const fs of Object.values(resolvedStates)) {
      for (const t of fs.trace ?? []) {
        if (seen.has(t.ruleId)) continue;
        seen.add(t.ruleId);
        out.push(t);
      }
    }
    return out;
  }, [resolvedStates]);

  // Sync state map for the Data slide-in (conduct only).
  const syncState = useMemo(() => {
    void tick;
    if (mode !== 'conduct') return undefined;
    const out: Record<string, ReturnType<PersistAdapter['getSyncState']>> = {};
    for (const f of forms) out[f._id] = persistAdapter.getSyncState(f._id);
    return out;
  }, [forms, mode, persistAdapter, tick]);

  const visibleForms = useMemo(
    () => initialVisibleForms.filter((f) => !resolvedStates[f._id]?.hidden),
    [initialVisibleForms, resolvedStates],
  );

  useEffect(() => {
    if (visibleForms.length > 0 && !visibleForms.some((f) => f._id === activeFormId)) {
      setActiveFormId(visibleForms[0]._id);
    }
  }, [activeFormId, visibleForms]);

  const activeForm = useMemo(
    () => visibleForms.find((f) => f._id === activeFormId) ?? visibleForms[0],
    [visibleForms, activeFormId],
  );

  // Stable openedAt for the Data slide-in (lives across renders).
  const openedAt = useMemo(() => new Date().toISOString(), []);

  if (!activeForm) {
    return <div role="alert" className="p-3 text-sm text-gray-600">No forms to render.</div>;
  }

  const activeResolved = resolvedStates[activeForm._id];

  // Build the field-id → field + value/isNA maps for the SectionRenderer.
  const fieldsById = useMemo(() => {
    const m: Record<string, typeof activeForm.fields[number]> = {};
    for (const fld of activeForm.fields) m[fld.id] = fld;
    return m;
  }, [activeForm]);

  const activeValuesObj = persistAdapter.getValues(activeForm._id);
  const valuesById = useMemo(() => {
    const m: Record<string, unknown> = {};
    for (const [id, fr] of Object.entries(activeValuesObj)) m[id] = fr.value;
    return m;
  }, [activeValuesObj]);
  const naById = useMemo(() => {
    const m: Record<string, boolean> = {};
    for (const [id, fr] of Object.entries(activeValuesObj)) m[id] = !!fr.isNA;
    return m;
  }, [activeValuesObj]);

  // Canonical completion verdict. Built from the SAME forms + resolved states
  // + persisted responses + logged occurrences the renderer already holds, fed
  // into the single shared `validateInspectionForCompletion` process. The host
  // consumes `verdict.canComplete` to hard-block its Complete control; it must
  // never re-derive completion gating itself (DRY / single-source-of-truth).
  const completionVerdict = useMemo<CompletionVerdict>(() => {
    void tick;
    return validateInspectionForCompletion(
      buildCompletionValidationInput(
        forms,
        resolvedStates,
        (formId) => persistAdapter.getValues(formId),
        persistAdapter.getViolations(),
      ),
    );
  }, [forms, resolvedStates, persistAdapter, tick]);

  useEffect(() => {
    onCompletionVerdictChange?.(completionVerdict);
  }, [completionVerdict, onCompletionVerdictChange]);

  // Per-form issue lists. Powers the active-form ValidationSummary panel AND
  // the per-tab issue-count badges. Derived directly from the completion
  // verdict's blocking issues so the on-screen issues NEVER diverge from what
  // the Complete gate enforces (single source of truth — `getFieldIssues` via
  // `validateInspectionForCompletion`, including occurrence-required issues).
  const issuesByFormId = useMemo<Record<string, FieldIssue[]>>(() => {
    const out: Record<string, FieldIssue[]> = {};
    for (const issue of completionVerdict.blocking) {
      // `formId` is optional on FieldIssue; issues without a formId cannot be grouped
      // per-form and are skipped here (they are still counted in the overall blocking list).
      if (!issue.formId) continue;
      (out[issue.formId] ?? (out[issue.formId] = [])).push(issue);
    }
    return out;
  }, [completionVerdict]);

  const activeIssues = issuesByFormId[activeForm._id] ?? [];
  const primaryInspectorOverride = useMemo(
    () => getPrimaryInspectorFromResponses(allResponses),
    [allResponses],
  );
  const effectiveInspectorId = primaryInspectorOverride?.id ?? inspectorId;

  function onFieldChange(fieldId: string, next: unknown): void {
    if (readOnly) return;
    const current = persistAdapter.getValues(activeForm._id);
    persistAdapter.setValues(activeForm._id, {
      ...current,
      [fieldId]: { ...current[fieldId], value: next },
    });
  }
  function onToggleNA(fieldId: string, isNA: boolean): void {
    if (readOnly) return;
    const current = persistAdapter.getValues(activeForm._id);
    persistAdapter.setValues(activeForm._id, {
      ...current,
      [fieldId]: { ...current[fieldId], isNA },
    });
  }

  const contacts: ContactEntry[] = contactPool ?? [];

  // ── Bound-mapping counts per fieldId across ALL forms. Drives whether the
  //    legacy violation fallbacks (code-only, codeless question) are
  //    unambiguous: a count > 1 means two bound mappings could claim the same
  //    legacy record, so fallbacks are disabled (the safe direction when a
  //    fieldId appears in more than one form).
  const boundMappingCountByFieldId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of forms) {
      for (const field of f.fields ?? []) {
        const bound = (field.violationMappings ?? [])
          .filter((m) => m.bindingMode !== 'pending-tenant-bind').length;
        if (bound > 0) counts.set(field.id, (counts.get(field.id) ?? 0) + bound);
      }
    }
    return counts;
  }, [forms]);

  // ── Inline violation-capture context. Wires the PersistAdapter into the
  //    new `<FieldViolationCaptures>` component that `<FieldRenderer>` mounts
  //    below every widget with `violationMappings`. The lookup is a strict
  //    priority over the field's violations: exact code+codeSetId match first,
  //    then — only when `allowsLegacyViolationFallback` says the mapping is
  //    the field's sole owner — legacy code-only and codeless question
  //    fallbacks (see findViolationForMapping).
  const violationsCaptureValue: ViolationsCaptureContextValue = useMemo(() => {
    void tick; // re-derive on adapter notifies so existing violations refresh
    const all = persistAdapter.getViolations();
    return {
      violations: all,
      inspectorId: effectiveInspectorId,
      eventIndex: currentEventIndex,
      readOnly: !!readOnly || !!bulkPilotMode,
      getViolation(fieldId, mapping) {
        return findViolationForMapping(
          all,
          fieldId,
          mapping,
          allowsLegacyViolationFallback(mapping, boundMappingCountByFieldId.get(fieldId) ?? 0),
        );
      },
      setViolation(fieldId, mapping, next) {
        const existing = findViolationForMapping(
          persistAdapter.getViolations(),
          fieldId,
          mapping,
          allowsLegacyViolationFallback(mapping, boundMappingCountByFieldId.get(fieldId) ?? 0),
        );
        if (next === null) {
          if (existing) persistAdapter.removeViolation(existing._id);
          return;
        }
        if (existing) {
          persistAdapter.updateViolation(existing._id, next as Partial<LoggedViolation>);
        } else {
          persistAdapter.addViolation(next);
        }
      },
    };
  }, [persistAdapter, effectiveInspectorId, currentEventIndex, readOnly, bulkPilotMode, boundMappingCountByFieldId, tick]);

  return (
    <RendererProviders attachmentPicker={attachmentPicker} resolveContext={resolveContext} isOffline={isOffline} inspectorRoster={inspectorRoster} thumbnailResolver={thumbnailResolver}>
     <ViolationsCaptureProvider value={violationsCaptureValue}>
      <div data-inspection-renderer data-mode={mode} className={showDataSlideIn ? 'grid grid-cols-[1fr_320px] h-full' : 'h-full'}>
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Form tab bar — each tab carries an issue-count badge driven by
              the same `getFieldIssues` predicate the per-widget wrappers use,
              so the badge stays in lockstep with what the inspector actually
              sees on screen. */}
          {visibleForms.length > 1 && (
            <nav
              role="tablist"
              data-form-tabs
              onWheel={(e) => {
                if (e.deltaY !== 0) {
                  e.currentTarget.scrollLeft += e.deltaY;
                }
              }}
              className="flex overflow-x-auto gap-1 px-3 py-2 border-b border-gray-200 bg-gray-50 scroll-smooth"
            >
              {visibleForms.map((f) => {
                const formIssues = issuesByFormId[f._id] ?? [];
                const isActive = f._id === activeForm._id;
                return (
                  <button
                    key={f._id}
                    type="button"
                    role="tab"
                    data-form-id={f._id}
                    data-form-issue-count={formIssues.length}
                    aria-selected={isActive}
                    onClick={() => setActiveFormId(f._id)}
                    className={`relative px-2 py-1 text-xs rounded-sm inline-flex items-center gap-1.5 ${isActive ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <span>{f.display?.sortLabel ?? f.name}</span>
                    {(mode === 'conduct' || mode === 'preview') && formIssues.length > 0 && (
                      <span
                        data-tab-issue-badge="true"
                        aria-label={`${formIssues.length} issue${formIssues.length === 1 ? '' : 's'}`}
                        className={`inline-flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-semibold rounded-full ${isActive ? 'bg-white text-blue-700' : 'bg-amber-500 text-white'}`}
                      >
                        {formIssues.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          )}

          <main className="flex-1 p-3" data-active-form-id={activeForm._id}>
            {(mode === 'conduct' || mode === 'preview') && activeForm.formKind !== 'module' && (
              <ValidationSummary issues={activeIssues} />
            )}
            {activeForm.formKind === 'module' ? (
              <ModuleRenderer
                form={activeForm}
                violations={persistAdapter.getViolations()}
                contacts={contacts}
                readOnly={!!readOnly || !!bulkPilotMode}
                onContactsChange={(nextContacts, changes) => {
                  for (const c of changes) persistAdapter.addContactChange(c);
                  onModuleContactsChange?.(nextContacts, changes);
                }}
                onViolationsChange={(nextViolations) => {
                  // Diff & flow into the adapter. The simplest correct path is
                  // to replace the additional-source violations wholesale.
                  const existingIds = new Set(persistAdapter.getViolations().filter((v) => v.source === 'additional').map((v) => v._id));
                  const nextIds = new Set(nextViolations.map((v) => v._id));
                  for (const v of nextViolations) {
                    if (!existingIds.has(v._id)) persistAdapter.addViolation(v);
                  }
                  for (const id of existingIds) {
                    if (!nextIds.has(id)) persistAdapter.removeViolation(id);
                  }
                }}
              />
            ) : (
              activeForm.layout.sections.map((section, index) => (
                <SectionRenderer
                  key={`${activeForm._id}:${section.id || 'section'}:${index}`}
                  formId={activeForm._id}
                  section={section}
                  fieldsById={fieldsById}
                  valuesById={valuesById}
                  naById={naById}
                  resolvedFields={activeResolved?.fields}
                  resolvedSection={activeResolved?.sections?.[section.id]}
                  showRequiredProgress={mode === 'conduct' || mode === 'preview'}
                  bulkPilotMode={bulkPilotMode}
                  density={density}
                  onFieldChange={onFieldChange}
                  onToggleNA={onToggleNA}
                  resetKey={resetKey}
                />
              ))
            )}
          </main>
        </div>

        {showDataSlideIn && (
          <DataSlideIn
            mode={mode}
            caseId={caseId}
            inspectorId={effectiveInspectorId}
            entityName={entityName}
            templateName={templateName}
            currentEventIndex={currentEventIndex}
            openedAt={openedAt}
            forms={visibleForms}
            resolvedStates={resolvedStates}
            rulesTrace={rulesTrace}
            responses={persistAdapter.getCanonicalResponses()}
            violations={persistAdapter.getViolations()}
            contactChanges={persistAdapter.getContactChanges()}
            syncState={syncState}
            onReset={mode === 'preview' ? (onReset ?? (() => persistAdapter.reset())) : undefined}
          />
        )}
      </div>
     </ViolationsCaptureProvider>
    </RendererProviders>
  );
}
